// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { DATA_FACET_COUNT } from './constants.js';
import { toArray, toBool, normalizeText, textFromNode } from './utils.js';

export function isNativeFormControl(control) {
  if (!control || !control.tagName) {
    return false;
  }

  var tag = control.tagName.toLowerCase();
  return tag === 'input' || tag === 'select' || tag === 'textarea';
}

// An option's visible text, minus any nested facet counter: a "(12)" placed inside the
// label must never become part of the filter value, or the option matches nothing.
function optionText(node) {
  if (!node || !node.querySelector(DATA_FACET_COUNT)) {
    return textFromNode(node);
  }

  var explicit = node.getAttribute('data-pf-value');
  if (explicit !== null) {
    return explicit;
  }

  var text = '';
  (function collect(el) {
    toArray(el.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        text += child.nodeValue;
      } else if (child.nodeType === 1 && !child.matches(DATA_FACET_COUNT)) {
        collect(child);
      }
    });
  })(node);
  return text.trim();
}

export function getChipValue(control) {
  return (
    control.getAttribute('data-pf-value') ||
    control.getAttribute('value') ||
    optionText(control) ||
    ''
  );
}

export function getNativeToggleValue(control) {
  if (!control) {
    return '';
  }

  var explicit = control.getAttribute('data-pf-value');
  if (explicit !== null && String(explicit).trim() !== '') {
    return String(explicit).trim();
  }

  var valueAttr = control.getAttribute('value');
  if (valueAttr !== null && String(valueAttr).trim() !== '') {
    return String(valueAttr).trim();
  }

  if (control.value && control.value !== 'on') {
    return String(control.value).trim();
  }

  var host = control.closest('label, .w-checkbox, .w-radio, [data-pf-option]');
  if (host) {
    var hostValue = host.getAttribute('data-pf-value');
    if (hostValue !== null && String(hostValue).trim() !== '') {
      return String(hostValue).trim();
    }

    var labelNode = host.querySelector('[data-pf-option-label], .w-form-label');
    var labelText = optionText(labelNode || host);
    if (labelText) {
      return labelText;
    }
  }

  return '';
}

export function getControlFilterValue(control) {
  if (!control) {
    return '';
  }

  if (!isNativeFormControl(control)) {
    return getChipValue(control);
  }

  var tag = control.tagName.toLowerCase();
  var type = (control.type || '').toLowerCase();
  if (type === 'checkbox' || type === 'radio') {
    return getNativeToggleValue(control);
  }

  if (tag === 'select') {
    return control.value || '';
  }

  return control.value || '';
}

export function getChipActiveClass(control) {
  return control.getAttribute('data-pf-active-class') || 'is-active';
}

export function isChipActive(control) {
  return control.classList.contains(getChipActiveClass(control));
}

export function setChipActive(control, isActive) {
  var activeClass = getChipActiveClass(control);
  control.classList.toggle(activeClass, isActive);
  control.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  control.setAttribute('data-pf-state', isActive ? 'active' : 'inactive');
}

export function isWildcardChip(control) {
  if (isNativeFormControl(control)) {
    return false;
  }

  return normalizeText(getChipValue(control)) === '*';
}

export function isChipExclusive(control) {
  return toBool(control.getAttribute('data-pf-exclusive'), false);
}

export function getChipGroup(control) {
  return normalizeText(control.getAttribute('data-pf-group') || '');
}

export function isControlDisabled(control) {
  if (!control) {
    return false;
  }

  if (control.disabled) {
    return true;
  }

  if (control.getAttribute('data-pf-disabled') === 'true') {
    return true;
  }

  return normalizeText(control.getAttribute('aria-disabled') || 'false') === 'true';
}

export function getValueFromControl(control) {
  if (!isNativeFormControl(control)) {
    if (!isChipActive(control)) {
      return [];
    }

    var chipValue = getChipValue(control);
    return chipValue ? [chipValue] : [];
  }

  var tag = control.tagName.toLowerCase();
  var type = (control.type || '').toLowerCase();

  if (tag === 'select' && control.multiple) {
    return toArray(control.options)
      .filter(function (opt) {
        return opt.selected;
      })
      .map(function (opt) {
        return opt.value;
      })
      .filter(Boolean);
  }

  if (type === 'checkbox' || type === 'radio') {
    if (!control.checked) {
      return [];
    }

    var toggleValue = getNativeToggleValue(control);
    return toggleValue ? [toggleValue] : [];
  }

  if (tag === 'select') {
    var selected = control.value;
    if (!selected || selected === '*') {
      return [];
    }
    return [selected];
  }

  var freeText = String(control.value || '').trim();
  if (!freeText) {
    return [];
  }

  return [freeText];
}

export function normalizeFilterControls(controls) {
  var result = [];
  var seen = new Set();

  toArray(controls).forEach(function (control) {
    if (!control || seen.has(control)) {
      return;
    }

    if (control.matches && control.matches(DATA_FACET_COUNT)) {
      return;
    }

    if (isNativeFormControl(control)) {
      seen.add(control);
      result.push(control);
      return;
    }

    var field = control.getAttribute('data-pf-filter');
    var proxyInput = control.querySelector('input[type="checkbox"], input[type="radio"]');

    if (proxyInput) {
      var proxyValue = control.getAttribute('data-pf-value');
      if (field && !proxyInput.hasAttribute('data-pf-filter')) {
        proxyInput.setAttribute('data-pf-filter', field);
      }
      if (proxyValue !== null && !proxyInput.hasAttribute('data-pf-value')) {
        proxyInput.setAttribute('data-pf-value', proxyValue);
      }
      if ((proxyInput.value === '' || proxyInput.value === 'on') && proxyValue !== null && String(proxyValue).trim() !== '') {
        proxyInput.value = String(proxyValue).trim();
      }

      if (!seen.has(proxyInput)) {
        seen.add(proxyInput);
        result.push(proxyInput);
      }
      return;
    }

    var nestedNative = control.querySelector(
      'input[data-pf-filter], select[data-pf-filter], textarea[data-pf-filter]'
    );
    if (nestedNative) {
      return;
    }

    seen.add(control);
    result.push(control);
  });

  return result;
}

export function syncWebflowToggleUi(control) {
  if (!control) {
    return;
  }

  var type = (control.type || '').toLowerCase();
  if (type !== 'checkbox' && type !== 'radio') {
    return;
  }

  var host = control.closest('label, .w-checkbox, .w-radio, [data-pf-option]');
  if (!host) {
    return;
  }

  var checked = !!control.checked;
  toArray(host.querySelectorAll('.w-checkbox-input, .w-radio-input')).forEach(function (node) {
    node.classList.toggle('w--redirected-checked', checked);
    node.setAttribute('aria-checked', checked ? 'true' : 'false');
  });
}

// The visible wrapper of a checkbox/radio — the node that carries its active class.
export function getToggleHost(control) {
  return (
    control.closest('[data-pf-option], .w-checkbox, .w-radio') ||
    control.closest('label')
  );
}

// A discrete filter option (checkbox, radio, chip) as the matcher sees it: its field, its
// normalized value, the match term _collectState would build for it, whether it is active,
// and the node its state classes live on. Null for text inputs, selects and the "*" wildcard.
export function describeOption(control) {
  var target;
  var active;

  if (!isNativeFormControl(control)) {
    target = control;
    active = isChipActive(control);
  } else {
    var type = (control.type || '').toLowerCase();
    if (type !== 'checkbox' && type !== 'radio') {
      return null;
    }
    target = getToggleHost(control) || control;
    active = !!control.checked;
  }

  var field = normalizeText(control.getAttribute('data-pf-filter') || '');
  var value = normalizeText(getControlFilterValue(control));
  if (!field || !value || value === '*') {
    return null;
  }

  return {
    field: field,
    value: value,
    term: {
      value: value,
      mode: normalizeText(control.getAttribute('data-pf-match') || '') === 'contains' ? 'contains' : 'exact',
    },
    active: active,
    target: target,
  };
}

export function syncNativeToggleState(control) {
  if (!control) {
    return;
  }

  var type = (control.type || '').toLowerCase();
  if (type !== 'checkbox' && type !== 'radio') {
    return;
  }

  var checked = !!control.checked;
  var activeClass = control.getAttribute('data-pf-active-class') || 'is-active';
  var host = getToggleHost(control);

  control.setAttribute('data-pf-state', checked ? 'active' : 'inactive');
  if (host) {
    host.classList.toggle(activeClass, checked);
    host.setAttribute('data-pf-state', checked ? 'active' : 'inactive');
  }

  syncWebflowToggleUi(control);
}

export function scheduleNativeToggleUiSync(control) {
  if (!control) {
    return;
  }

  syncNativeToggleState(control);

  var raf = window.requestAnimationFrame;
  if (typeof raf === 'function') {
    raf(function () {
      syncNativeToggleState(control);
      raf(function () {
        syncNativeToggleState(control);
      });
    });
    return;
  }

  window.setTimeout(function () {
    syncNativeToggleState(control);
  }, 0);
}

export function toggleNativeBinaryControl(control, type, root) {
  if (!control) {
    return false;
  }

  if (type === 'checkbox') {
    control.checked = !control.checked;
    return true;
  }

  if (type === 'radio') {
    if (control.checked) {
      return false;
    }

    if (control.name) {
      var scopeRoot = root || control.form || document;
      // Compare names in JS: a name with a quote would make an attribute selector throw.
      toArray(scopeRoot.querySelectorAll('input[type="radio"]')).forEach(function (radio) {
        if (radio.name === control.name) {
          radio.checked = false;
        }
      });
    }

    control.checked = true;
    return true;
  }

  return false;
}

export function setControlValue(control, rawValues) {
  if (!isNativeFormControl(control)) {
    var normalizedChip = normalizeText(getChipValue(control));
    var shouldBeActive = rawValues
      .map(normalizeText)
      .indexOf(normalizedChip) !== -1;
    setChipActive(control, shouldBeActive);
    return;
  }

  var normalizedValues = rawValues.map(normalizeText);
  var tag = control.tagName.toLowerCase();
  var type = (control.type || '').toLowerCase();

  if (tag === 'select' && control.multiple) {
    toArray(control.options).forEach(function (opt) {
      opt.selected = normalizedValues.indexOf(normalizeText(opt.value)) !== -1;
    });
    return;
  }

  if (type === 'checkbox' || type === 'radio') {
    control.checked = normalizedValues.indexOf(normalizeText(getNativeToggleValue(control))) !== -1;
    syncNativeToggleState(control);
    return;
  }

  if (tag === 'select') {
    var target = toArray(control.options).find(function (opt) {
      return normalizedValues.indexOf(normalizeText(opt.value)) !== -1;
    });
    control.value = target ? target.value : '*';
    return;
  }

  control.value = rawValues[0] || '';
}

export function neutralizeFacetCountInteraction(node) {
  if (!node) {
    return;
  }

  node.removeAttribute('role');
  node.removeAttribute('tabindex');
  node.removeAttribute('aria-pressed');
  node.setAttribute('data-pf-interactive', 'false');
  node.style.pointerEvents = 'none';
}
